import { FC, PropsWithChildren, useCallback, useMemo } from 'react';

import { useWorkspaceStore } from '@/stores';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';

import { useAccountFinancialValueDisplayPreference } from './accountFinancialValueDisplay';
import { FinancialValueDisplayContext, FinancialValueDisplayContextValue } from './financialValueDisplayContextState';
import { resolveFinancialValueDisplayPreference } from './formatFinancialValue';

interface FinancialValueDisplayProviderProps extends PropsWithChildren {
    surfaceKey?: string;
}

export const FinancialValueDisplayProvider: FC<FinancialValueDisplayProviderProps> = ({ children, surfaceKey }) => {
    const { preferences, setPreference } = useUserPreferences();
    const { preference: accountPreference } = useAccountFinancialValueDisplayPreference();
    const workspacePreference = useWorkspaceStore((state) => state.selectedWorkspace?.financialValueDisplay);
    const surfacePreference = surfaceKey
        ? preferences?.financialOverview?.views?.[surfaceKey]?.financialValueDisplay
        : undefined;
    const setSurfacePreference = useCallback(
        (nextValue: typeof surfacePreference) => {
            if (surfaceKey) {
                setPreference(`financialOverview.views.${surfaceKey}.financialValueDisplay`, nextValue ?? null);
            }
        },
        [setPreference, surfaceKey],
    );
    const value = useMemo<FinancialValueDisplayContextValue>(
        () => ({
            ...resolveFinancialValueDisplayPreference({
                account: accountPreference,
                surface: surfacePreference,
                workspace: workspacePreference,
            }),
            ...(surfaceKey ? { setSurfacePreference, surfacePreference: surfacePreference ?? null } : {}),
        }),
        [accountPreference, setSurfacePreference, surfaceKey, surfacePreference, workspacePreference],
    );

    return <FinancialValueDisplayContext.Provider value={value}>{children}</FinancialValueDisplayContext.Provider>;
};
